import React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import SentimentSatisfiedAltOutlinedIcon from '@mui/icons-material/SentimentSatisfiedAltOutlined';
import LoadingState from '../../dashboard/LoadingState';
import ErrorState from '../../dashboard/ErrorState';
import PermissionDenied from '../../dashboard/PermissionDenied';
import { getDepartmentName, useDepartments } from '../../../hooks/useDepartments';
import { sentimentChipSx } from '../../../utils/sentiment';
import { formatDate, formatPhone, formatRelative } from '../../../utils/formatters';
import ContactAvatar from '../ContactAvatar';
import ContactStatusChips from '../ContactStatusChips';
import ContactTemperatureChip from '../ContactTemperatureChip';
import { useContactQuery } from '../useContactQueries';

const PURPOSES = ['sale', 'question', 'schedule', 'undefined'];

const sentimentKey = (sentiment) => {
  if (sentiment > 0) return 'positive';
  if (sentiment < 0) return 'negative';
  return 'neutral';
};

function Stat({ label, value }) {
  return (
    <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>
      <Typography variant="h6">{value}</Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

function IdentityCard({ contact, actions }) {
  const { t, i18n } = useTranslation();
  const location = [contact.city, contact.state].filter(Boolean).join(', ');
  const rows = [
    {
      key: 'phone',
      icon: <PhoneOutlinedIcon />,
      value: contact.phone_number ? formatPhone(contact.phone_number) : null,
    },
    { key: 'email', icon: <EmailOutlinedIcon />, value: contact.email },
    { key: 'location', icon: <LocationOnOutlinedIcon />, value: location },
  ];

  return (
    <Card>
      <CardHeader
        avatar={<ContactAvatar name={contact.display_name} size={56} />}
        title={contact.display_name || t('notAvailable')}
        subheader={`${t('contacts.fields.contactSince')}: ${formatDate(contact.created_at, i18n.language, { empty: t('notAvailable') })}`}
        slotProps={{ title: { variant: 'h6' } }}
        action={<ContactStatusChips contact={contact} sx={{ pt: 1, pr: 1 }} />}
      />
      <CardContent sx={{ pt: 0 }}>
        <List disablePadding>
          {rows.map((row) => (
            <ListItem key={row.key} disableGutters>
              <ListItemIcon>{row.icon}</ListItemIcon>
              <ListItemText
                primary={t(`contacts.fields.${row.key}`)}
                secondary={row.value || t('notAvailable')}
                slotProps={{ primary: { variant: 'caption', color: 'text.secondary' }, secondary: { variant: 'body2', color: 'text.primary' } }}
              />
            </ListItem>
          ))}
        </List>
      </CardContent>
      {actions && <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>{actions}</CardActions>}
    </Card>
  );
}

function ActivityCard({ contact }) {
  const { t, i18n } = useTranslation();
  const stats = contact.stats ?? {};
  const relationship = contact.relationship ?? {};
  const relative = (value) => formatRelative(value, i18n.language, { empty: t('notAvailable') });

  return (
    <Card>
      <CardHeader title={t('contacts.stats.title')} />
      <CardContent sx={{ pt: 0 }}>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))' }}>
          <Stat label={t('contacts.stats.conversations')} value={stats.total_conversations ?? 0} />
          <Stat label={t('contacts.stats.messages')} value={stats.total_messages ?? 0} />
          <Stat label={t('contacts.stats.firstSeen')} value={relative(stats.first_seen_at)} />
          <Stat label={t('contacts.stats.lastMessage')} value={relative(stats.last_message_at)} />
          <Stat label={t('contacts.stats.timesAttendedByMe')} value={relationship.times_attended_by_me ?? 0} />
          <Stat label={t('contacts.stats.lastAttendedAt')} value={relative(relationship.last_attended_at)} />
          <Stat label={t('contacts.stats.collaborators')} value={relationship.collaborators_count ?? 0} />
        </Box>
      </CardContent>
    </Card>
  );
}

function InsightsCard({ insights }) {
  const { t } = useTranslation();
  const { data: departments = [] } = useDepartments();
  const hasSentiment = insights.last_sentiment !== null && insights.last_sentiment !== undefined;
  const hasDeadline = insights.decision_deadline_days !== null && insights.decision_deadline_days !== undefined;
  const rows = [
    {
      key: 'department',
      value: insights.last_department && getDepartmentName(departments, insights.last_department),
    },
    { key: 'targetBusiness', value: insights.last_target_business },
    {
      key: 'purpose',
      value: insights.last_purpose && (PURPOSES.includes(insights.last_purpose)
        ? t(`contacts.insights.purposes.${insights.last_purpose}`)
        : insights.last_purpose),
    },
    { key: 'summary', value: insights.last_summary },
    {
      key: 'decisionDeadline',
      value: hasDeadline && t('contacts.insights.days', { count: insights.decision_deadline_days }),
    },
  ].filter((row) => row.value);
  const isEmpty = !insights.last_temperature && !hasSentiment && rows.length === 0;

  return (
    <Card>
      <CardHeader title={t('contacts.insights.title')} subheader={t('contacts.insights.hint')} />
      <CardContent sx={{ pt: 0 }}>
        {isEmpty ? (
          <Typography variant="body2" color="text.secondary">
            {t('contacts.insights.empty')}
          </Typography>
        ) : (
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <ContactTemperatureChip value={insights.last_temperature} />
              {hasSentiment && (
                <Chip
                  size="small"
                  icon={<SentimentSatisfiedAltOutlinedIcon />}
                  label={`${t('contacts.insights.sentiment')}: ${t(`contacts.insights.sentiments.${sentimentKey(insights.last_sentiment)}`)}`}
                  sx={sentimentChipSx(insights.last_sentiment)}
                />
              )}
            </Stack>
            <List disablePadding>
              {rows.map((row) => (
                <ListItem key={row.key} disableGutters>
                  <ListItemText
                    primary={t(`contacts.insights.${row.key}`)}
                    secondary={row.value}
                    slotProps={{ primary: { variant: 'caption', color: 'text.secondary' }, secondary: { variant: 'body2', color: 'text.primary' } }}
                  />
                </ListItem>
              ))}
            </List>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Contact detail body (identity, activity, AI insights), meant to be shared
 * by the dashboard (/dashboard/contacts/:uuid) and, later, the mobile client
 * detail: each page only adds its own shell. `renderActions(contact)` returns
 * the page's buttons, rendered at the end of the identity card.
 */
export default function ContactDetailContent({ uuid, renderActions }) {
  const { t } = useTranslation();
  const { data, isLoading, isError, error, refetch } = useContactQuery(uuid);

  if (isLoading) {
    return <LoadingState message={t('contacts.states.loading')} />;
  }

  if (isError) {
    const status = error?.response?.status;
    if (status === 403) {
      return (
        <PermissionDenied
          title={t('contacts.states.permissionDeniedTitle')}
          message={t('contacts.states.permissionDeniedMessage')}
        />
      );
    }
    if (status === 404) {
      return (
        <ErrorState
          title={t('contacts.states.notFoundTitle')}
          message={t('contacts.states.notFoundMessage')}
        />
      );
    }
    return (
      <ErrorState
        title={t('contacts.states.errorTitle')}
        message={t('contacts.states.errorMessage')}
        actionLabel={t('contacts.states.retry')}
        onRetry={refetch}
      />
    );
  }

  return (
    <Stack spacing={3} sx={{ width: '100%' }}>
      <IdentityCard contact={data} actions={renderActions?.(data)} />
      <ActivityCard contact={data} />
      {data.ai_insights && <InsightsCard insights={data.ai_insights} />}
    </Stack>
  );
}
